/**
 * Skafe Card Quick Add & Live Quantity Cart Manager (Optimistic UI Edition)
 *
 * Provides zero-latency optimistic feedback, instant sessionStorage cache hydration,
 * synchronous multi-card state synchronization, optimistic header cart count updates,
 * number-go-up micro-animations, floating +1 particles, and a robust async per-variant queue.
 */

(function () {
  'use strict';

  const CACHE_KEY = 'skafe_cart_cache_v1';

  class SkafeCartManager {
    constructor() {
      // Maps variantId (Number) -> optimistic quantity (Number)
      this.variantCounts = new Map();
      // Maps variantId (Number) -> confirmed server quantity (Number)
      this.serverCounts = new Map();
      // Maps variantId (Number) -> { inFlight: boolean, nextTarget: number | null, debounceTimer: any }
      this.syncQueues = new Map();

      this.cartData = null;
      this.initialized = false;
      this.subscribers = new Set();

      // 1. Synchronously hydrate from cache immediately (0ms latency on page transitions)
      this.hydrateFromCache();

      // 2. Schedule background initialization and Shopify server reconciliation
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }

      this.setupMutationObserver();
    }

    hydrateFromCache() {
      try {
        const cached = sessionStorage.getItem(CACHE_KEY) || localStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && Array.isArray(parsed.items)) {
            this.updateFromCartData(parsed, false, false);
          }
        }
      } catch (err) {
        // Cache miss or invalid JSON, ignore
      }
    }

    saveToCache(cartData) {
      if (!cartData) return;
      try {
        const payload = JSON.stringify({
          item_count: cartData.item_count ?? this.getTotalItemCount(),
          items: (cartData.items || []).map((it) => ({
            id: Number(it.id || it.variant_id),
            quantity: Number(it.quantity || 0),
          })),
        });
        sessionStorage.setItem(CACHE_KEY, payload);
        localStorage.setItem(CACHE_KEY, payload);
      } catch (err) {
        // Ignore quota errors
      }
    }

    setupMutationObserver() {
      if (typeof MutationObserver === 'undefined') return;
      const observer = new MutationObserver((mutations) => {
        let hasNewCards = false;
        for (const m of mutations) {
          if (m.addedNodes.length > 0) {
            for (const node of m.addedNodes) {
              if (node.nodeType === 1) {
                if (node.matches?.('skafe-card-quick-add, [data-card-qty-badge]') || node.querySelector?.('skafe-card-quick-add, [data-card-qty-badge]')) {
                  hasNewCards = true;
                  break;
                }
              }
            }
          }
          if (hasNewCards) break;
        }
        if (hasNewCards) {
          this.notifyAll();
        }
      });

      if (document.body) {
        observer.observe(document.body, { childList: true, subtree: true });
      } else {
        document.addEventListener('DOMContentLoaded', () => {
          observer.observe(document.body, { childList: true, subtree: true });
        });
      }
    }

    async init() {
      if (this.initialized) return;
      this.initialized = true;

      // Subscribe to Dawn pubsub cart updates if available
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cartData) {
            this.updateFromCartData(event.cartData, false);
          } else {
            this.fetchCart();
          }
        });
      }

      // Initial cart fetch to reconcile with Shopify server only if cart cookie or items exist
      if (document.cookie.indexOf('cart=') !== -1 || this.variantCounts.size > 0) {
        await this.fetchCart();
      }
    }

    async fetchCartRaw() {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
      if (!response.ok) return null;
      return response.json();
    }

    async fetchCart() {
      try {
        const cart = await this.fetchCartRaw();
        if (cart) {
          this.updateFromCartData(cart, false, true);
          this.saveToCache(cart);
        }
      } catch (err) {
        console.warn('[SkafeCartManager] Failed to fetch cart state:', err);
      }
    }

    updateFromCartData(cartData, notify = true, updateServerCounts = true) {
      if (!cartData) return;
      this.cartData = cartData;
      const newCounts = new Map();

      if (Array.isArray(cartData.items)) {
        for (const item of cartData.items) {
          const variantId = Number(item.id || item.variant_id);
          if (variantId) {
            newCounts.set(variantId, (newCounts.get(variantId) || 0) + Number(item.quantity || 0));
          }
        }
      }

      this.variantCounts = newCounts;
      if (updateServerCounts) {
        this.serverCounts = new Map(newCounts);
      }

      this.notifyAll();
      this.updateOptimisticHeaderCart();
    }

    getQuantity(variantId) {
      return this.variantCounts.get(Number(variantId)) || 0;
    }

    getServerQuantity(variantId) {
      return this.serverCounts.get(Number(variantId)) || 0;
    }

    getTotalItemCount() {
      let total = 0;
      for (const qty of this.variantCounts.values()) {
        total += qty;
      }
      return total;
    }

    notifyAll() {
      document.querySelectorAll('skafe-card-quick-add').forEach((el) => {
        if (typeof el.syncWithCart === 'function') el.syncWithCart(false);
      });
      document.querySelectorAll('[data-card-qty-badge]').forEach((badge) => {
        const variantId = Number(badge.dataset.variantId);
        const qty = this.getQuantity(variantId);
        const countSpan = badge.querySelector('[data-badge-count]');
        if (countSpan) countSpan.textContent = String(qty);
        badge.classList.toggle('is-active', qty > 0);
        badge.hidden = qty === 0;
      });
    }

    /**
     * Trigger visual floating +1 effect at the element's screen location
     */
    spawnFlyPlusOne(element, text = '+1') {
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const flyEl = document.createElement('div');
      flyEl.className = 'skafe-fly-plus-one';
      flyEl.textContent = text;
      flyEl.style.left = `${rect.left + rect.width / 2}px`;
      flyEl.style.top = `${rect.top + rect.height / 2}px`;

      document.body.appendChild(flyEl);
      flyEl.addEventListener('animationend', () => flyEl.remove(), { once: true });
      setTimeout(() => flyEl.remove(), 1000);
    }

    /**
     * Optimistically update header cart bubble count and animate it instantly
     */
    updateOptimisticHeaderCart() {
      const totalCount = this.getTotalItemCount();
      const bubble = document.querySelector('#cart-icon-bubble');
      if (!bubble) return;

      let countBubble = bubble.querySelector('.cart-count-bubble');
      if (totalCount > 0) {
        if (!countBubble) {
          countBubble = document.createElement('div');
          countBubble.className = 'cart-count-bubble';
          countBubble.innerHTML = `<span aria-hidden="true">${totalCount}</span><span class="visually-hidden">${totalCount} itens no carrinho</span>`;
          bubble.appendChild(countBubble);
        } else {
          const numSpan = countBubble.querySelector('span[aria-hidden="true"]');
          if (numSpan) numSpan.textContent = totalCount < 100 ? String(totalCount) : '99+';
          const visSpan = countBubble.querySelector('.visually-hidden');
          if (visSpan) visSpan.textContent = `${totalCount} itens no carrinho`;
        }
      } else if (countBubble) {
        countBubble.remove();
      }
    }

    /**
     * Trigger bump animation on the header cart bubble
     */
    bumpHeaderCart() {
      const bubble = document.querySelector('#cart-icon-bubble');
      if (!bubble) return;
      bubble.classList.remove('skafe-cart-bump');
      void bubble.offsetWidth; // trigger reflow
      bubble.classList.add('skafe-cart-bump');
    }

    /**
     * Optimistically update quantity with zero latency and process backend sync queue
     */
    setQuantity(variantId, targetQty, triggerElement = null) {
      const numId = Number(variantId);
      const prevOptimistic = this.getQuantity(numId);
      const isIncrement = targetQty > prevOptimistic;

      // 1. INSTANT OPTIMISTIC STATE UPDATE (0ms)
      if (targetQty > 0) {
        this.variantCounts.set(numId, targetQty);
      } else {
        this.variantCounts.delete(numId);
      }

      // 2. Synchronize all matching cards & badges across the DOM instantly
      document.querySelectorAll(`skafe-card-quick-add[data-variant-id="${numId}"]`).forEach((card) => {
        if (typeof card.renderState === 'function') {
          card.renderState(targetQty, true);
        }
      });
      document.querySelectorAll(`[data-card-qty-badge][data-variant-id="${numId}"]`).forEach((badge) => {
        const countSpan = badge.querySelector('[data-badge-count]');
        if (countSpan) countSpan.textContent = String(targetQty);
        badge.classList.toggle('is-active', targetQty > 0);
        badge.hidden = targetQty === 0;
        if (targetQty > 0) {
          badge.classList.remove('is-bumping');
          void badge.offsetWidth;
          badge.classList.add('is-bumping');
        }
      });

      // 3. Optimistic Header Cart Update & Animations (0ms)
      this.updateOptimisticHeaderCart();
      if (isIncrement && triggerElement) {
        const delta = targetQty - prevOptimistic;
        this.spawnFlyPlusOne(triggerElement, delta > 1 ? `+${delta}` : '+1');
        this.bumpHeaderCart();
      } else if (isIncrement) {
        this.bumpHeaderCart();
      }

      // 4. Update session storage cache optimistically
      this.saveToCache({
        item_count: this.getTotalItemCount(),
        items: Array.from(this.variantCounts.entries()).map(([id, quantity]) => ({ id, quantity })),
      });

      // 5. Enqueue backend sync
      this.enqueueSync(numId, targetQty);
    }

    enqueueSync(variantId, targetQty) {
      let queue = this.syncQueues.get(variantId);
      if (!queue) {
        queue = { inFlight: false, nextTarget: null, debounceTimer: null };
        this.syncQueues.set(variantId, queue);
      }

      queue.nextTarget = targetQty;

      if (queue.debounceTimer) {
        clearTimeout(queue.debounceTimer);
      }

      queue.debounceTimer = setTimeout(() => {
        queue.debounceTimer = null;
        this.processQueue(variantId);
      }, 150);
    }

    async processQueue(variantId) {
      const queue = this.syncQueues.get(variantId);
      if (!queue || queue.inFlight) return;

      const targetQty = queue.nextTarget;
      const serverQty = this.getServerQuantity(variantId);

      if (targetQty === serverQty) {
        return; // already in sync
      }

      queue.inFlight = true;

      try {
        const sectionsToRender = [];
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((sec) => sectionsToRender.push(sec.id));
        } else {
          sectionsToRender.push('cart-drawer', 'cart-icon-bubble');
        }

        const isFirstAdd = serverQty === 0 && targetQty > 0;
        const endpoint = isFirstAdd
          ? `${window.routes?.cart_add_url || '/cart/add'}.js`
          : `${window.routes?.cart_change_url || '/cart/change'}.js`;

        const body = isFirstAdd
          ? JSON.stringify({
              items: [{ id: variantId, quantity: targetQty }],
              sections: sectionsToRender,
              sections_url: window.location.pathname,
            })
          : JSON.stringify({
              id: String(variantId),
              quantity: targetQty,
              sections: sectionsToRender,
              sections_url: window.location.pathname,
            });

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body,
        });

        const updatedCart = await res.json();

        if (updatedCart.status && updatedCart.status !== 200) {
          // Server rejected the update (e.g. inventory limit or sold out)
          console.warn('[SkafeCartManager] Server error on cart sync:', updatedCart.message || updatedCart.description);
          // Rollback to server quantity
          this.variantCounts.set(variantId, serverQty);
          if (serverQty === 0) this.variantCounts.delete(variantId);
          this.notifyAll();
          this.updateOptimisticHeaderCart();
          this.showErrorToast(updatedCart.message || updatedCart.description || 'Limite de estoque atingido.');
          return;
        }

        // Update server quantity to confirmed target
        this.serverCounts.set(variantId, targetQty);
        if (targetQty === 0) this.serverCounts.delete(variantId);

        // Update drawer HTML in background
        if (cartDrawer && updatedCart.sections) {
          cartDrawer.getSectionsToRender?.().forEach((section) => {
            const secEl = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (secEl && updatedCart.sections[section.id]) {
              secEl.innerHTML = cartDrawer.getSectionInnerHTML(updatedCart.sections[section.id], section.selector);
            }
          });
          // The section HTML is fresh, but the <cart-drawer> root keeps the
          // server-rendered `is-empty` class from page load. While it sticks,
          // CSS hides the header and the whole items form and strips the inner
          // padding until a full reload. /cart/change.js answers with the full
          // cart (authoritative); /cart/add.js answers with the line item, so
          // fall back to the requested quantity (an add guarantees non-empty).
          if (typeof updatedCart.item_count === 'number') {
            cartDrawer.classList.toggle('is-empty', updatedCart.item_count === 0);
          } else if (targetQty > 0) {
            cartDrawer.classList.remove('is-empty');
          }
        }

        // Update header cart icon bubble if returned by server
        const bubbleSection = document.getElementById('shopify-section-cart-icon-bubble');
        if (bubbleSection && updatedCart.sections?.['cart-icon-bubble']) {
          bubbleSection.innerHTML = new DOMParser()
            .parseFromString(updatedCart.sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section')?.innerHTML || updatedCart.sections['cart-icon-bubble'];
        }

        // add.js returns the created line item; change.js returns the full cart object.
        const cartState = isFirstAdd ? await this.fetchCartRaw() : updatedCart;
        if (cartState) {
          this.updateFromCartData(cartState, false, true);
          this.saveToCache(cartState);
          // Authoritative empty-state sync once the full cart is known (covers
          // the case where the last unit of the last product was removed).
          if (cartDrawer && typeof cartState.item_count === 'number') {
            cartDrawer.classList.toggle('is-empty', cartState.item_count === 0);
          }
        }

        if (cartState && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'skafe-card-quick-add',
            productVariantId: variantId,
            cartData: cartState,
          });
        }

        // Same confirmation as the PDP: the first unit added from a card opens the
        // drawer. Stepper +/- afterwards stay inline so shoppers can keep adding.
        if (isFirstAdd && cartState && cartDrawer && typeof cartDrawer.open === 'function') {
          const trigger = [...document.querySelectorAll(`skafe-card-quick-add[data-variant-id="${variantId}"] [data-action="increment"]`)].find(
            (btn) => btn.offsetParent,
          );
          cartDrawer.open(trigger);
        }
      } catch (err) {
        console.error('[SkafeCartManager] Network error updating cart:', err);
      } finally {
        queue.inFlight = false;
        // If the user clicked again while the request was in flight, process next target
        if (queue.nextTarget !== this.getServerQuantity(variantId)) {
          this.processQueue(variantId);
        }
      }
    }

    showErrorToast(message) {
      let toast = document.querySelector('.skafe-cart-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'skafe-cart-toast';
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      toast.classList.add('is-visible');
      setTimeout(() => toast.classList.remove('is-visible'), 3200);
    }
  }

  window.SkafeCartManager = new SkafeCartManager();

  class SkafeCardQuickAdd extends HTMLElement {
    connectedCallback() {
      this.variantId = Number(this.dataset.variantId);
      this.productId = Number(this.dataset.productId);
      this.addBtn = this.querySelector('[data-action="add-initial"]');
      this.stepper = this.querySelector('[data-card-stepper]');
      this.minusBtn = this.querySelector('[data-action="decrement"]');
      this.plusBtn = this.querySelector('[data-action="increment"]');
      this.countEl = this.querySelector('[data-stepper-count]');
      this.cardWrapper = this.closest('.card-wrapper') || this.closest('.card');

      this.bindEvents();
      this.syncWithCart(false);
    }

    bindEvents() {
      if (this.addBtn) {
        this.addBtn.addEventListener('click', this.onAddInitial.bind(this));
      }
      if (this.plusBtn) {
        this.plusBtn.addEventListener('click', this.onIncrement.bind(this));
      }
      if (this.minusBtn) {
        this.minusBtn.addEventListener('click', this.onDecrement.bind(this));
      }
    }

    syncWithCart(animate = false) {
      if (!this.variantId) return;
      const qty = window.SkafeCartManager.getQuantity(this.variantId);
      this.renderState(qty, animate);
    }

    renderState(qty, animate = true) {
      if (this.countEl) {
        const currentCount = Number(this.countEl.textContent || '0');
        this.countEl.textContent = String(qty);
        if (animate && qty !== currentCount) {
          this.countEl.classList.remove('is-bumping');
          void this.countEl.offsetWidth; // trigger reflow
          this.countEl.classList.add('is-bumping');
        }
      }

      if (qty > 0) {
        if (this.addBtn) this.addBtn.hidden = true;
        if (this.stepper) this.stepper.hidden = false;
        if (this.cardWrapper) this.cardWrapper.classList.add('has-items-in-cart');
      } else {
        if (this.addBtn) this.addBtn.hidden = false;
        if (this.stepper) this.stepper.hidden = true;
        if (this.cardWrapper) this.cardWrapper.classList.remove('has-items-in-cart');
      }

      // Also sync media badge on the card
      if (this.cardWrapper) {
        const badge = this.cardWrapper.querySelector('[data-card-qty-badge]');
        if (badge) {
          const countSpan = badge.querySelector('[data-badge-count]');
          if (countSpan) countSpan.textContent = String(qty);
          badge.classList.toggle('is-active', qty > 0);
          badge.hidden = qty === 0;

          if (animate && qty > 0) {
            badge.classList.remove('is-bumping');
            void badge.offsetWidth;
            badge.classList.add('is-bumping');
          }
        }

        if (animate && qty > 0) {
          this.cardWrapper.classList.remove('skafe-card-pulse');
          void this.cardWrapper.offsetWidth;
          this.cardWrapper.classList.add('skafe-card-pulse');
        }
      }
    }

    onAddInitial(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.variantId) return;

      this.renderState(1, true);
      window.SkafeCartManager.setQuantity(this.variantId, 1, this.addBtn);
    }

    onIncrement(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.variantId) return;

      const currentQty = window.SkafeCartManager.getQuantity(this.variantId);
      const nextQty = currentQty + 1;
      this.renderState(nextQty, true);
      window.SkafeCartManager.setQuantity(this.variantId, nextQty, this.plusBtn);
    }

    onDecrement(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.variantId) return;

      const currentQty = window.SkafeCartManager.getQuantity(this.variantId);
      const nextQty = Math.max(0, currentQty - 1);
      this.renderState(nextQty, true);
      window.SkafeCartManager.setQuantity(this.variantId, nextQty, this.minusBtn);
    }
  }

  if (!customElements.get('skafe-card-quick-add')) {
    customElements.define('skafe-card-quick-add', SkafeCardQuickAdd);
  }
})();
